import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Check, Sparkles, Rocket } from 'lucide-react';
import { ContactFormModal } from '@/components/modals/ContactFormModal';
import { motion, AnimatePresence } from 'framer-motion';

interface ATSPlansModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const plans = [
  {
    name: 'Starter',
    tagline: 'Para equipos que recién empiezan a estructurar su reclutamiento.',
    price: '$49',
    priceSuffix: '/mes',
    priceNote: 'Facturación mensual · USD',
    description: 'Cubra sus necesidades esenciales de contratación. Simple y efectivo.',
    cta: 'Empezar gratis',
    ctaNote: 'Sin tarjeta de crédito, sin obligación.',
    features: [
      'Usuarios ilimitados',
      'Equipos colaborativos',
      '3 publicaciones activas',
      'Multiposting en +17 job boards',
      'AI Candidate Matching*',
      'Notificaciones vía email',
      'Perfil de empresa personalizado',
    ],
    popular: false,
  },
  {
    name: 'Growth',
    tagline: 'Para empresas en expansión que necesitan escalar contrataciones.',
    price: '$129',
    priceSuffix: '/mes',
    priceNote: 'Facturación mensual · USD',
    description: '¿Escalando? Expandamos juntos sus capacidades de contratación.',
    cta: 'Empezar gratis',
    ctaNote: 'Sin tarjeta de crédito, sin obligación.',
    features: [
      'Todo lo del plan Starter',
      '10 publicaciones activas',
      'Análisis de CV/currículum*',
      'AI Candidate Matching*',
      'Preguntas de filtrado',
      'Pipelines personalizables',
      '@Mencionar miembros del equipo',
      'Feed de actividad de candidatos',
      'Cuestionarios y evaluaciones personalizables',
      'Archivo de documentos del candidato',
      'Reportería avanzada de uso',
      'Landing page personalizada',
    ],
    popular: true,
  },
  {
    name: 'Pro',
    tagline: 'Para empresas que necesitan dominio total del proceso de contratación.',
    price: '$249',
    priceSuffix: '/mes',
    priceNote: 'Facturación mensual · USD',
    description: 'Dominio total de la contratación. Suite completa para su eficiencia.',
    cta: 'Empezar gratis',
    ctaNote: 'Sin tarjeta de crédito, sin obligación.',
    features: [
      'Todo lo del plan Growth',
      'Publicaciones ilimitadas',
      'Video entrevistas',
      'Scorecards de entrevista',
      'Acciones automatizadas por etapa',
      'Cuestionarios de selección',
      'Reportería avanzada',
      'Notas privadas',
      'Acceso a pruebas psicométricas ilimitadas',
      'Dominio web propio personalizado',
      'Plantillas de trabajo propias',
    ],
    popular: false,
  },
];

const REGISTER_URL = 'https://app.nexushr.tech/login/new';

const ATSPlansModal: React.FC<ATSPlansModalProps> = ({ isOpen, onClose }) => {
  const [consultationModalOpen, setConsultationModalOpen] = useState(false);

  const handleGetStarted = () => {
    window.open(REGISTER_URL, '_blank', 'noopener,noreferrer');
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto p-0 gap-0 border-0">
          {/* Header con gradiente */}
          <div className="bg-gradient-to-r from-primary/10 via-turquoise-500/10 to-primary/10 px-6 py-8 text-center border-b border-border/50">
            <div className="flex items-center justify-center gap-2 mb-3">
              <Sparkles className="w-6 h-6 text-primary" />
            <DialogHeader>
              <DialogTitle className="font-exo text-2xl md:text-3xl font-bold text-foreground">
                Planes Nexus ATS
              </DialogTitle>
            </DialogHeader>
              <Sparkles className="w-6 h-6 text-turquoise-500" />
            </div>
            <p className="text-muted-foreground text-sm max-w-md mx-auto">
              Elegí el plan que mejor se adapte a las necesidades de tu empresa
            </p>
          </div>
          
          {/* Cards de planes */}
          <div className="p-6 md:p-8">
            <div className="grid md:grid-cols-3 gap-6">
            <AnimatePresence>
              {plans.map((plan, planIndex) => {
                return (
                  <motion.div 
                    key={plan.name}
                    initial={{ opacity: 0, y: 30, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ 
                      duration: 0.4, 
                      delay: planIndex * 0.15,
                      ease: [0.25, 0.46, 0.45, 0.94]
                    }}
                    className="relative rounded-2xl border-2 border-border/60 p-6 flex flex-col transition-all duration-300 hover:shadow-xl hover:-translate-y-1 hover:border-primary/40"
                  >
                    {/* Badge popular */}
                    {plan.popular && (
                      <motion.div 
                        className="absolute -top-3.5 left-1/2 -translate-x-1/2"
                        initial={{ opacity: 0, scale: 0 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.3, delay: 0.5 }}
                      >
                        <span className="bg-primary text-primary-foreground text-xs font-bold px-4 py-1.5 rounded-full shadow-lg shadow-primary/30 flex items-center gap-1.5">
                          <Sparkles className="w-3 h-3" />
                          MÁS POPULAR
                        </span>
                      </motion.div>
                    )}
                    
                    {/* Nombre y descripción */}
                    <div className="text-center mb-5 pt-2">
                      <h3 className="font-exo text-2xl font-bold text-foreground mb-1">
                        {plan.name}
                      </h3>
                      <p className="text-xs text-muted-foreground leading-relaxed min-h-[2.5rem]">
                        {plan.tagline}
                      </p>
                    </div>

                    {/* Botón CTA */}
                    <Button 
                      className={`w-full mb-3 h-12 font-semibold text-base transition-all duration-300 ${
                        plan.popular 
                          ? 'bg-gradient-to-r from-primary to-turquoise-500 hover:from-primary/90 hover:to-turquoise-500/90 shadow-lg shadow-primary/25' 
                          : ''
                      }`}
                      variant={plan.popular ? 'default' : 'outline'}
                      onClick={handleGetStarted}
                    >
                      {plan.cta}
                    </Button>

                    <p className="text-xs text-muted-foreground text-center mb-5">
                      {plan.ctaNote}
                    </p>

                    {/* Lista de características */}
                    <div className="border-t border-border/50 pt-5 flex-1">
                      <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-4">
                        Incluye:
                      </p>
                      <ul className="space-y-3">
                        {plan.features.map((feature, index) => (
                          <motion.li 
                            key={index} 
                            className="flex items-start gap-3 group"
                            initial={{ opacity: 0, x: -10 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ 
                              duration: 0.3, 
                              delay: (planIndex * 0.15) + (index * 0.03) + 0.3
                            }}
                          >
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${
                              plan.popular 
                                ? 'bg-gradient-to-br from-primary to-turquoise-500' 
                                : 'bg-primary/10'
                            }`}>
                              <Check className={`w-3 h-3 ${plan.popular ? 'text-white' : 'text-primary'}`} />
                            </div>
                            <span className="text-sm text-foreground/80 group-hover:text-foreground transition-colors">
                              {feature}
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                    </div>

                    {/* Footer del card */}
                    <div className="border-t border-border/50 mt-5 pt-4">
                      <button
                        onClick={() => setConsultationModalOpen(true)}
                        className="text-sm text-primary hover:text-primary/80 w-full text-center font-medium transition-colors flex items-center justify-center gap-1 group"
                      >
                        ¿Preguntas? 
                        <span className="group-hover:underline">Hablá con un humano</span>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
            </div>

            {/* Nota de límites */}
            <p className="text-xs text-muted-foreground text-center mt-6">
              *Aplican límites de uso. Upgrade disponible para uso adicional.
            </p>

            {/* CTA para startups */}
            <div className="mt-8 bg-gradient-to-r from-muted/80 via-muted/50 to-muted/80 rounded-2xl p-6 md:p-8 text-center border border-border/50">
              <div className="flex items-center justify-center gap-2 mb-3">
                <Rocket className="w-5 h-5 text-primary" />
                <h4 className="font-exo text-xl font-bold">
                  ¿Empresa en etapa inicial?
                </h4>
              </div>
              <p className="text-sm text-muted-foreground mb-5 max-w-md mx-auto">
                Consultá por nuestros planes especiales para startups y empresas en crecimiento.
              </p>
              <Button 
                variant="outline" 
                onClick={() => setConsultationModalOpen(true)}
                className="border-primary/30 hover:bg-primary/5 hover:border-primary"
              >
                Contactar ventas
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <ContactFormModal
        isOpen={consultationModalOpen}
        onClose={() => setConsultationModalOpen(false)}
        type="consultation"
        title="Hablá con un experto"
        subtitle="Te ayudamos a encontrar el plan ideal para tu empresa"
      />
    </>
  );
};

export default ATSPlansModal;
